import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js'

const PHONE_COUNTRY_US = 'US' as const
const PHONE_COUNTRY_CA = 'CA' as const

export function normalizePhoneToE164(input: string | undefined): string | null {
  const value = input?.trim()
  if (!value) return null

  // A leading "+" lets libphonenumber infer the country; otherwise parse as US national.
  const phone = parsePhoneNumberFromString(value, PHONE_COUNTRY_US)
  if (phone?.isValid()) return phone.number

  const digitsOnly = value.replace(/[^\d]/g, '')

  if (!value.startsWith('+')) {
    // Some valid CA area codes (e.g. 236) fail US-region validation; try CA first.
    const ca = parsePhoneNumberFromString(value, PHONE_COUNTRY_CA)
    if (ca?.isValid()) return ca.number

    // Legacy-compatible fallback: accept a bare 10-digit NANP number as +1XXXXXXXXXX.
    if (digitsOnly.length === 10) return `+1${digitsOnly}`
  }

  // 11-digit NANP with leading "1": outside the +-guard so "+1…" matches its bare form.
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1'))
    return `+${digitsOnly}`

  return null
}

/**
 * Reduces a phone filter query to its digits so a value copied from a
 * table's formatted display (e.g. "(510) 988-2788") contains-matches the
 * stored E.164 form ("+15109882788"). Returns "" when the query holds no
 * digits at all; what an unmatchable query should send is the caller's
 * decision, since a backend comparing digits reads a blank query as "no
 * filter" rather than "no match".
 */
export function normalizePhoneFilterQuery(input: string): string {
  return input.replace(/\D/g, '')
}

export function formatPhoneForDisplay(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return ''

  const formatter = trimmed.startsWith('+')
    ? new AsYouType()
    : new AsYouType(PHONE_COUNTRY_US)

  return formatter.input(trimmed)
}
